import { type Biome, sampleBiomeForCell } from './biome-transition';
import { originDistance } from './distance';
/**
 * Biome layer (ADR-0013): Voronoi cells on a coarse hex grid + Markov
 * transition matrix with 8-level discrete priority resolution. Pure
 * function of `(seed, q, r)`; cross-chunk agreement falls out by
 * construction.
 *
 * Coarse grid model
 * -----------------
 * Each coarse-cell `C = (cq_L, cr_L)` covers the axial parallelogram
 * `[cq_L · L, (cq_L+1) · L) × [cr_L · L, (cr_L+1) · L)`. The cell holds
 * exactly one seed point, jittered from the parallelogram centre by up
 * to `JITTER_CAP_RATIO · L` per axial component. A fine-hex's Voronoi
 * cell membership is the coarse-cell whose seed point is nearest in
 * axial hex distance.
 *
 * Membership search — 9 candidates, not the ADR's 7
 * --------------------------------------------------
 * ADR-0013 §3 claims `C` plus its 6 hex-neighbour coarse-cells suffices
 * because the 0.4·L jitter bound makes it exhaustive. Empirically that
 * holds for a true hex coarse-tiling but **not** for the
 * `floor(q/L) / floor(r/L)` parallelogram tiling the ADR also specifies.
 * The parallelogram has two corners (bottom-right (L-1, L-1) and top-left
 * (0, 0)) that touch coarse-cells `(+1, +1)` and `(-1, -1)` — these are at
 * coarse-axial distance 2 (a 2-ring neighbour), so they're not in the
 * 6-hex-neighbour list. With jitter, a seed in either diagonal cell can
 * pull closer to a fine-hex at that corner than any 1-ring seed.
 *
 * Concretely: fine-hex `(L-1, L-1)` in cell `(0, 0)`. 1-ring `(1, 0)`'s
 * best seed sits at axial distance `≈ L/2 + 1`; jittered away it grows
 * to `≈ 0.9·L + 1`. 2-ring `(1, 1)`'s seed jittered toward the fine-hex
 * shrinks to `≈ 0.2·L + 2`. `(1, 1)` wins.
 *
 * The membership search therefore includes 9 candidates: `C` + the 6 hex
 * neighbours + the two parallelogram-diagonals `(+1, +1)` and `(-1, -1)`.
 * Determinism and cross-chunk agreement are unaffected — biome remains a
 * pure function of `(seed, q, r)`; this is just exhaustive coverage of
 * the candidate set. (Mirror in code: see `COARSE_NEIGHBOUR_OFFSETS` and
 * `COARSE_DIAGONAL_OFFSETS` below.)
 */
import { hashCombine, mulberry32 } from './noise';

export type { Biome } from './biome-transition';
export {
  BIOMES,
  TRANSITION_MATRIX,
  BIOME_PRIOR,
  BIOME_PRIOR_INNER,
  BIOME_PRIOR_OUTER,
  BIOME_PRIOR_NEAR_DIST,
  BIOME_PRIOR_FAR_DIST,
  EVIL_WEIGHT,
} from './biome-transition';

export interface CellId {
  readonly cq: number;
  readonly cr: number;
}

/** Characteristic cell radius — ADR-0013 §2 locks ≈ 32 hexes (≈ 4 chunks across). */
export const CELL_SCALE_L = 32;
/** Jitter cap per axial component, as a fraction of L (ADR-0013 §3). */
export const JITTER_CAP_RATIO = 0.4;
/** Discrete resolution levels per ADR-0013 §3: `floor(8 · hash01(seed, cell))`. */
export const MAX_LEVELS = 8;

const COARSE_NEIGHBOUR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** Parallelogram-tiling diagonals — see file header for why these are required. */
const COARSE_DIAGONAL_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [-1, -1],
];

const JITTER_SALT = 0xa11c_0de1;
const LEVEL_SALT = 0xb10e_1e02;

function cellBaseHash(seed: number, cq: number, cr: number): number {
  let h = seed >>> 0;
  h = hashCombine(h, cq | 0);
  h = hashCombine(h, cr | 0);
  return h;
}

/** Fractional axial coords of the Voronoi seed point inside coarse-cell `(cq, cr)`. */
export function cellSeedPoint(
  seed: number,
  cq: number,
  cr: number,
): { readonly sq: number; readonly sr: number } {
  const h = hashCombine(cellBaseHash(seed, cq, cr), JITTER_SALT);
  const rng = mulberry32(h);
  const cap = JITTER_CAP_RATIO * CELL_SCALE_L;
  const jq = (rng() * 2 - 1) * cap;
  const jr = (rng() * 2 - 1) * cap;
  const centreQ = cq * CELL_SCALE_L + CELL_SCALE_L / 2;
  const centreR = cr * CELL_SCALE_L + CELL_SCALE_L / 2;
  return { sq: centreQ + jq, sr: centreR + jr };
}

/** Discrete resolution level in `[0, MAX_LEVELS)` for coarse-cell `(cq, cr)`. */
export function cellLevel(seed: number, cq: number, cr: number): number {
  const h = hashCombine(cellBaseHash(seed, cq, cr), LEVEL_SALT);
  return Math.floor(MAX_LEVELS * mulberry32(h)());
}

function axialDistance(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq;
  const dr = ar - br;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** Coarse-cell containing fine-hex `(q, r)` per `floor(q/L), floor(r/L)`. */
function coarseHexOf(q: number, r: number): CellId {
  return {
    cq: Math.floor(q / CELL_SCALE_L),
    cr: Math.floor(r / CELL_SCALE_L),
  };
}

/**
 * Voronoi cell membership for fine-hex `(q, r)`. Scans 9 candidate coarse-
 * cells (see header) and returns the one whose seed point is nearest in
 * axial hex distance. Ties resolved by `(cq, cr)` lex order so the
 * function is total and deterministic.
 */
export function cellIdAt(seed: number, q: number, r: number): CellId {
  const home = coarseHexOf(q, r);
  let bestCq = home.cq;
  let bestCr = home.cr;
  const home0 = cellSeedPoint(seed, home.cq, home.cr);
  let bestDist = axialDistance(q, r, home0.sq, home0.sr);
  const candidates: ReadonlyArray<readonly [number, number]> = [
    ...COARSE_NEIGHBOUR_OFFSETS,
    ...COARSE_DIAGONAL_OFFSETS,
  ];
  for (const [dq, dr] of candidates) {
    const ncq = home.cq + dq;
    const ncr = home.cr + dr;
    const sp = cellSeedPoint(seed, ncq, ncr);
    const d = axialDistance(q, r, sp.sq, sp.sr);
    if (d < bestDist || (d === bestDist && (ncq < bestCq || (ncq === bestCq && ncr < bestCr)))) {
      bestDist = d;
      bestCq = ncq;
      bestCr = ncr;
    }
  }
  return { cq: bestCq, cr: bestCr };
}

// ─── biome resolution ────────────────────────────────────────────────────

const seedMemos: Map<number, Map<string, Biome>> = new Map();

function memoFor(seed: number): Map<string, Biome> {
  let m = seedMemos.get(seed >>> 0);
  if (!m) {
    m = new Map();
    seedMemos.set(seed >>> 0, m);
  }
  return m;
}

function cellKey(cq: number, cr: number): string {
  return `${cq},${cr}`;
}

/**
 * Resolve the biome for a cell. Walks the discrete-level dependency DAG
 * by recursing into strictly-lower-level neighbour cells (memoised). Max
 * recursion depth = `MAX_LEVELS - 1 = 7` by construction (ADR-0013 §3).
 *
 * Higher- or equal-level neighbours are ignored at sample time: cell `c`
 * at level `k` only sees its level-`<k` neighbours' resolutions. That
 * keeps the dependency DAG acyclic and bounded.
 */
function resolveBiome(seed: number, cq: number, cr: number): Biome {
  const memo = memoFor(seed);
  const key = cellKey(cq, cr);
  const cached = memo.get(key);
  if (cached !== undefined) return cached;

  const myLevel = cellLevel(seed, cq, cr);
  const lowerNeighbours: Biome[] = [];
  for (const [dq, dr] of COARSE_NEIGHBOUR_OFFSETS) {
    const ncq = cq + dq;
    const ncr = cr + dr;
    if (cellLevel(seed, ncq, ncr) < myLevel) {
      lowerNeighbours.push(resolveBiome(seed, ncq, ncr));
    }
  }
  const cellOriginDist = originDistance(cq * CELL_SCALE_L, cr * CELL_SCALE_L);
  const biome = sampleBiomeForCell(seed, cq, cr, lowerNeighbours, cellOriginDist);
  memo.set(key, biome);
  return biome;
}

/**
 * Biome for fine-hex `(q, r)` under world `seed`. Pure function;
 * cross-chunk agreement holds because both inputs (cell membership and
 * cell biome) are themselves pure on `(seed, …)`.
 */
export function biomeAt(seed: number, q: number, r: number): Biome {
  const cell = cellIdAt(seed, q, r);
  return resolveBiome(seed, cell.cq, cell.cr);
}

/** Resolution catchment — the closure of strictly-decreasing-level
 * neighbour walks from `cell`. Bounded by the 8-level construction. */
export function resolutionCatchment(seed: number, cell: CellId): ReadonlySet<string> {
  const visited = new Set<string>();
  const stack: Array<readonly [number, number]> = [[cell.cq, cell.cr]];
  while (stack.length > 0) {
    const top = stack.pop();
    if (!top) break;
    const [cq, cr] = top;
    const key = cellKey(cq, cr);
    if (visited.has(key)) continue;
    visited.add(key);
    const myLevel = cellLevel(seed, cq, cr);
    for (const [dq, dr] of COARSE_NEIGHBOUR_OFFSETS) {
      if (cellLevel(seed, cq + dq, cr + dr) < myLevel) {
        stack.push([cq + dq, cr + dr]);
      }
    }
  }
  return visited;
}

/** Test-only: clear the per-seed memo so tests can probe the resolver
 * without cross-test interference. Not for production callers. */
export function _clearBiomeCacheForTests(): void {
  seedMemos.clear();
}
